import {
  Alert,
  AlertDescription,
} from "@babackapp/customer-ui/components/alert";
import {
  AnimatedButtonContainer,
  AnimatedButtonRow,
  Button,
} from "@babackapp/customer-ui/components/button";
import {
  Header,
  HeaderDescription,
  HeaderTitle,
} from "@babackapp/customer-ui/components/header";
import {
  InputGroup,
  InputGroupAddon,
  InputGroupInput,
  InputGroupLabel,
} from "@babackapp/customer-ui/components/input-group";
import { WidgetPage } from "@babackapp/customer-ui/components/widget-page";
import { zodResolver } from "@hookform/resolvers/zod";
import {
  AlertCircleIcon,
  AtSignIcon,
  GiftIcon,
  LoaderIcon,
  PhoneIcon,
  UserIcon,
} from "lucide-react";
import { motion } from "motion/react";
import { Controller, useForm } from "react-hook-form";
import { useFocusFirstError } from "../hooks/use-focus-first-error";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { useCustomText } from "../hooks/use-custom-text";
import {
  giftIdentificationSchema,
  type GiftIdentificationFormValues,
} from "../forms/gift-identification.form";

// giftCode is free (a bare z.string()): it never fails, so it has no error line.
const GIFT_FIELDS = ["last_name", "first_name", "email", "phone"] as const;
const GIFT_FIELD_LABELS: Record<(typeof GIFT_FIELDS)[number], string> = {
  last_name: "common.fields.lastName",
  first_name: "common.fields.firstName",
  email: "common.fields.email",
  phone: "common.fields.phone",
};
const giftErrorId = (name: (typeof GIFT_FIELDS)[number]) => `gift-identification-${name}-error`;

export function GiftIdentificationPage() {
  const { shopId } = useParams<{ shopId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("gift-return-token") ?? undefined;
  const t = useCustomText();

  const form = useForm<GiftIdentificationFormValues>({
    resolver: zodResolver(giftIdentificationSchema),
    defaultValues: { giftCode: "", last_name: "", first_name: "", email: "", phone: "" },
    // useFocusFirstError focuses the field once its message is tied (BBS-438).
    shouldFocusError: false,
  });
  const { control, handleSubmit, getValues, formState: { isSubmitting, errors } } = form;
  useFocusFirstError(form, GIFT_FIELDS);

  const onSubmit = (values: GiftIdentificationFormValues) => {
    navigate("address", {
      state: {
        token,
        giftCode: values.giftCode || undefined,
        last_name: values.last_name,
        first_name: values.first_name,
        email: values.email,
        phone: values.phone,
      },
    });
  };

  return (
    <WidgetPage variant="centered">
      <Header>
        <HeaderTitle>{t("gift.identification.title")}</HeaderTitle>
        <HeaderDescription>{t("gift.identification.description")}</HeaderDescription>
      </Header>

      {Object.keys(errors).length > 0 && (
        <Alert variant="destructive">
          <AlertCircleIcon />
          <AlertDescription>
            {GIFT_FIELDS.flatMap((name) =>
              errors[name]
                ? [
                    // Each field in error points at its own line (BBS-438). zod's
                    // default messages are English-only: the text is ours.
                    <div key={name} id={giftErrorId(name)}>
                      {name === "email" && getValues("email")
                        ? t("address.error.emailInvalid")
                        : t("common.error.fieldRequired", { field: t(GIFT_FIELD_LABELS[name]) })}
                    </div>,
                  ]
                : [],
            )}
          </AlertDescription>
        </Alert>
      )}

      <form className="flex flex-col gap-4" onSubmit={handleSubmit(onSubmit)}>
        <motion.div
          initial={{ opacity: 0, x: 15 }}
          animate={{ opacity: 1, x: 0, transition: { delay: 0.1 } }}
          exit={{ opacity: 0, x: -15 }}
          transition={{ duration: 0.3 }}
          className="flex flex-col gap-3"
        >
          {!token && (
            <Controller
              control={control}
              name="giftCode"
              render={({ field, fieldState }) => (
                <InputGroup className="h-auto">
                  <InputGroupInput placeholder="GIFT-XXXX" {...field} aria-invalid={!!fieldState.error} />
                  <InputGroupAddon align="block-start">
                    <InputGroupLabel>{t("gift.identification.giftCode")}</InputGroupLabel>
                    <GiftIcon />
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
          )}
          <div className="grid grid-cols-2 gap-2">
            <Controller
              control={control}
              name="last_name"
              render={({ field, fieldState }) => (
                <InputGroup className="h-auto">
                  <InputGroupInput placeholder={t("common.fields.lastNameShort")} {...field} aria-invalid={!!fieldState.error}
                    aria-describedby={fieldState.error ? giftErrorId("last_name") : undefined} />
                  <InputGroupAddon align="block-start">
                    <InputGroupLabel>{t("common.fields.lastName")}</InputGroupLabel>
                    <UserIcon />
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
            <Controller
              control={control}
              name="first_name"
              render={({ field, fieldState }) => (
                <InputGroup className="h-auto">
                  <InputGroupInput placeholder={t("common.fields.firstName")} {...field} aria-invalid={!!fieldState.error}
                    aria-describedby={fieldState.error ? giftErrorId("first_name") : undefined} />
                  <InputGroupAddon align="block-start">
                    <InputGroupLabel>{t("common.fields.firstName")}</InputGroupLabel>
                    <UserIcon />
                  </InputGroupAddon>
                </InputGroup>
              )}
            />
          </div>
          <Controller
            control={control}
            name="email"
            render={({ field, fieldState }) => (
              <InputGroup className="h-auto">
                <InputGroupInput type="email" placeholder="email@example.com" {...field} aria-invalid={!!fieldState.error}
                    aria-describedby={fieldState.error ? giftErrorId("email") : undefined} />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.email")}</InputGroupLabel>
                  <AtSignIcon />
                </InputGroupAddon>
              </InputGroup>
            )}
          />
          <Controller
            control={control}
            name="phone"
            render={({ field, fieldState }) => (
              <InputGroup className="h-auto">
                <InputGroupInput type="tel" placeholder="+33 6 00 00 00 00" {...field} aria-invalid={!!fieldState.error}
                    aria-describedby={fieldState.error ? giftErrorId("phone") : undefined} />
                <InputGroupAddon align="block-start">
                  <InputGroupLabel>{t("common.fields.phone")}</InputGroupLabel>
                  <PhoneIcon />
                </InputGroupAddon>
              </InputGroup>
            )}
          />
        </motion.div>

        <AnimatedButtonRow variants={{}}>
          <AnimatedButtonContainer layoutId="line-item-confirm-button" variants={{}}>
            <Button type="submit" disabled={isSubmitting}>
              {isSubmitting ? <LoaderIcon className="animate-spin" /> : t("common.continue")}
            </Button>
          </AnimatedButtonContainer>
        </AnimatedButtonRow>
      </form>

      <button
        type="button"
        className="font-content text-sm text-muted-foreground hover:text-foreground underline mt-2 text-center"
        onClick={() => navigate(`/return-widget/${shopId}`)}
      >
        {t("gift.notAGift")}
      </button>
    </WidgetPage>
  );
}
